import { Button, color } from "@funded-here-interface/common";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { useStyle } from "../../styles/InvestorDocUpload";
import { supplierData } from "./Data";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { File as FileEnum } from "@funded-here-interface/common/src/constant/enum";
import FileUploadComponent from "../../components/FileUpload/FileUpload";
import { DocumentsUpload } from "@funded-here-interface/common/src/constant/type";

// Custom hooks and utilities
import { useDraftSync } from "./hooks/useDraftSync";
import { useOrgSubmission } from "./hooks/useOrgSubmission";
import {
  extractFilePaths,
  validateSupplierDocumentsWithErrors,
  getExistingFilesForType,
} from "./utils/documentHelpers";

const SupplierDocsUpload: FC = () => {
  const { classes } = useStyle();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const navigate = useNavigate();

  // API hooks
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // Redux selectors
  const { userId, kycStatus, orgId, token, type } = useSelector(
    (state: RootState) => state.auth
  );

  // Document state from Redux
  const {
    business: businessProfile,
    certificateOfIncorporation,
    proofOfBankAccountDetails,
    supportingDocumentsWithSuppliers,
  } = useSelector((state: RootState) => state.bordoc);

  // Business and bank state from Redux
  const distributorBizState = useSelector((state: RootState) => state.disBiz);
  const distributorBankState = useSelector((state: RootState) => state.disBank);

  // Custom hooks
  const { syncDraftToRedux } = useDraftSync();
  const { submitOrganization } = useOrgSubmission({
    setIsLoading,
    onError: (error) => toast.error(error.message),
  });

  const hasFetched = useRef(false);

  /**
   * Load draft data from backend and sync to Redux
   */
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!hasFetched.current && userId) {
        try {
          const data = await getOrgDraftMutation.mutateAsync({
            userId: parseInt(userId),
            token,
          });

          if (data && isMounted) {
            syncDraftToRedux(data);
            hasFetched.current = true;
          }
        } catch (error) {
          toast.error(`Error loading draft: ${error}`);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Save draft to backend
   */
  const saveDraft = (showToast: boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        businessProfile,
        certificateOfIncorporation,
        proofOfBankAccountDetails,
        supportingDocumentsWithSuppliers,
      },
      {
        onSuccess: () => {
          if (showToast) {
            toast.success("Draft Saved");
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  const documents = [...supplierData];

  /**
   * Clear error for a specific field
   */
  const handleClearError = (errorKey: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[errorKey];
      return newErrors;
    });
  };

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    saveDraft();
    navigate("/supplier-bank-details");
  };

  /**
   * Handle next - validate documents and submit organization
   */
  const handleNext = async () => {
    saveDraft(false);

    // Validate all required documents are uploaded
    const documentErrors = validateSupplierDocumentsWithErrors({
      businessProfile,
      certificateOfIncorporation,
      proofOfBankAccountDetails,
      supportingDocumentsWithSuppliers,
    });

    if (Object.keys(documentErrors).length > 0) {
      setErrors(documentErrors);
      toast.error("Please upload all required documents");
      return;
    }

    // Clear errors if validation passes
    setErrors({});

    // Build documents upload object with file paths
    const documentsUpload: DocumentsUpload = {
      businessProfile: extractFilePaths(businessProfile),
      certificateOfIncorporation: extractFilePaths(certificateOfIncorporation),
      proofOfBankAccountDetails: extractFilePaths(proofOfBankAccountDetails),
      supportingDocumentsWithSuppliers: extractFilePaths(
        supportingDocumentsWithSuppliers
      ),
    };

    // Submit organization (create or update based on KYC status)
    submitOrganization({
      kycStatus: kycStatus || null,
      orgId: orgId?.toString() || null,
      token,
      onBoardingUserType: type as string,
      bizState: distributorBizState,
      bankState: distributorBankState,
      documentsUpload,
    });
  };

  return (
    <div>
      <p className={classes.headinfo}>
        All uploads must be in PDF format. The file size should not exceed 10MB
        each.
        <br />
        We may request for additional supporting documents beyond those listed
        here as part of our onboarding and verification process.
      </p>
      <div className={classes.stepContent} style={{ background: "#fff" }}>
        {documents.map((item) => (
          <FileUploadComponent
            key={item.number}
            number={item.number}
            fileId={item.id}
            FileName={item.heading}
            isSupplier={true}
            errors={errors}
            onClearError={handleClearError}
            existingFiles={getExistingFilesForType(item.id, {
              businessProfile,
              certificateOfIncorporation,
              proofOfBankAccountDetails,
              supportingDocumentsWithSuppliers,
            })}
          />
        ))}
        <div className={classes.buttonDiv2}>
          <div className={classes.buttonWrapper}>
            <Button
              onClick={handleBack}
              backgroundColor={color.WHITE}
              textColor={color.FHGREEN}
              children="Back"
              border="1px solid #338F86"
              width="210px"
            />
          </div>
          <div className={classes.buttonWrapper}>
            <Button
              disabled={isLoading}
              onClick={handleNext}
              textColor={color.WHITE}
              backgroundColor={color.FHGREEN}
              children="Submit"
              width="210px"
            />
          </div>
          <div className={classes.buttonWrapper}>
            <Button
              onClick={saveDraft}
              textColor={color.WHITE}
              backgroundColor={color.LOANTEXT}
              children="Save Draft"
              width="100%"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierDocsUpload;
